import { isBrazeInitializedAtom, Logger, sessionAtom, useSettings } from '@capsicum/common';
import { useAtom, useAtomValue } from 'jotai';
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { BrazeModule } from '../../services/customer-engagement/BrazeModule';
import { env } from '../../util';

import { PermissionStatusEnum } from './WebPushContext';

const WEB_BRAZE_API_KEY = env.NEXT_PUBLIC_BRAZE_WEB_API_KEY;
const BRAZE_CUSTOM_SDK_ENDPOINT = env.NEXT_PUBLIC_BRAZE_ENDPOINT;
const ENABLE_LOGGING = env.NEXT_PUBLIC_BRAZE_ENABLE_LOGGING === '1';

export const BrazePushNotificationProvider = ({ children }: PropsWithChildren) => {
	const {
		settings: { isBrazeEnabled, isPushNotificationsEnabled }
	} = useSettings();
	const [notificationStatus, setNotificationStatus] = useState<NotificationPermission>(
		typeof window !== 'undefined' && 'Notification' in window
			? Notification.permission
			: PermissionStatusEnum.DENIED
	);

	const [isBrazeInitialized, setIsBrazeInitialized] = useAtom(isBrazeInitializedAtom);

	const userId = useAtomValue(sessionAtom)?.userId;

	const initBraze = useCallback(() => {
		try {
			if (
				!WEB_BRAZE_API_KEY ||
				!BRAZE_CUSTOM_SDK_ENDPOINT ||
				!userId ||
				!isBrazeEnabled ||
				!isPushNotificationsEnabled ||
				notificationStatus === PermissionStatusEnum.DENIED ||
				!BrazeModule
			)
				return;

			BrazeModule.initialize(WEB_BRAZE_API_KEY, {
				allowUserSuppliedJavascript: true,
				baseUrl: BRAZE_CUSTOM_SDK_ENDPOINT,
				enableLogging: ENABLE_LOGGING,
				manageServiceWorkerExternally: true
			});

			BrazeModule.changeUser(userId);

			BrazeModule.automaticallyShowInAppMessages();
			BrazeModule.openSession();

			BrazeModule.requestPushPermission(
				() => {
					BrazeModule?.getUser()?.setPushNotificationSubscriptionType(
						BrazeModule?.User.NotificationSubscriptionTypes.OPTED_IN
					);
					setIsBrazeInitialized(true);
					Logger.info(`Subscribed and OptedIn on Braze`);
				},
				() => {
					Logger.warn(`Braze subscription went wrong.`);
				}
			);
		} catch (e) {
			Logger.error(`Subscription Braze failed with error: ${e}`);
		}
	}, [userId, notificationStatus, isBrazeEnabled, isPushNotificationsEnabled]);

	const unsubscribeBraze = () => {
		if (isBrazeInitialized && BrazeModule) {
			// setPushNotificationSubscriptionType will unsubscribe all devices related to the user
			BrazeModule.getUser()?.setPushNotificationSubscriptionType(
				BrazeModule.User.NotificationSubscriptionTypes.UNSUBSCRIBED
			);
			BrazeModule.destroy();
			setIsBrazeInitialized(false);
			Logger.info('Unsubscribed from Braze');
		}
	};

	useEffect(() => {
		if (isPushNotificationsEnabled && isBrazeEnabled) {
			if (typeof navigator !== 'undefined' && navigator?.permissions?.query) {
				navigator.permissions.query({ name: 'notifications' }).then(permissionStatus => {
					permissionStatus.onchange = () => {
						const status: NotificationPermission =
							permissionStatus.state === 'prompt' ? PermissionStatusEnum.DEFAULT : permissionStatus.state;

						setNotificationStatus(status);
					};
				});
			}
		}
	}, [isPushNotificationsEnabled, isBrazeEnabled, isPushNotificationsEnabled]);

	useEffect(() => {
		initBraze();
		if (notificationStatus === PermissionStatusEnum.DENIED || !userId) unsubscribeBraze();
	}, [userId, notificationStatus, isBrazeEnabled, isPushNotificationsEnabled]);

	return children;
};
